import { Alert, Button, Group, NumberInput, SimpleGrid, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, podatki, type Shema } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import { Kartica, StanjeNalaganja } from '../postavitev/Podatki';
import { cas } from './skrbnistvo';
import { StripeSkrbnistvo } from './StripeSkrbnistvo';
import { glave, obNapaki } from './zeton';

type Nastavitve = Shema['PlacilneNastavitve'];

const KLJUC = ['skrbnik', 'placila'];

/** Skrbništvo → Plačila: nakazilo na naš TRR (UPN QR), strošek obdelave Stripa in povezava s Stripom. */
export function PlacilaSkrbnistvo() {
  const nastavitve = useQuery({
    queryKey: KLJUC,
    queryFn: async ({ signal }) => {
      try {
        return await podatki(api.GET('/api/skrbnik/placila', { signal, headers: glave() }));
      } catch (e) {
        obNapaki(e);
        throw e;
      }
    },
  });
  return (
    <Stack gap="md">
      {!nastavitve.data ? (
        <Kartica>
          <StanjeNalaganja
            napaka={nastavitve.error}
            ponovi={() => void nastavitve.refetch()}
            naslov="Nastavitev plačil ni bilo mogoče naložiti"
          />
        </Kartica>
      ) : (
        <NastavitveTrr key={nastavitve.data.spremenjeno ?? 0} n={nastavitve.data} />
      )}
      <StripeSkrbnistvo />
    </Stack>
  );
}

function NastavitveTrr({ n }: { n: Nastavitve }) {
  const odjemalec = useQueryClient();
  const [urejeno, setUrejeno] = useState({
    trrIban: n.trrIban ?? '',
    prejemnik: n.prejemnik ?? '',
    ulica: n.ulica ?? '',
    kraj: n.kraj ?? '',
    email: n.email ?? '',
    stripeStrosek: (n.stripeStrosek ?? '') as number | string,
  });
  const [napake, setNapake] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);
  const polje = (k: keyof typeof urejeno) => ({
    value: urejeno[k] as string,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setUrejeno({ ...urejeno, [k]: e.currentTarget.value }),
    error: napake[k],
  });

  const shrani = async () => {
    setDela(true);
    setNapake({});
    setNapaka(null);
    try {
      const novo = await podatki(
        api.PUT('/api/skrbnik/placila', {
          headers: glave(),
          body: {
            ...urejeno,
            stripeStrosek: urejeno.stripeStrosek === '' ? undefined : Number(urejeno.stripeStrosek),
          },
        }),
      );
      odjemalec.setQueryData(KLJUC, novo);
      notifications.show({ color: 'green', message: 'Nastavitve plačil so shranjene.' });
    } catch (e) {
      obNapaki(e);
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setNapake(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setDela(false);
    }
  };

  return (
    <Kartica naslov="Nakazilo na naš račun">
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Stranke na zaslonu Naročnina vidijo naš TRR s sklicem svoje instance in kodo UPN QR (samo če so vpisani TRR,
          prejemnik in kraj). Prejeto nakazilo vnesete pri instanci (kartica Naročnina in točke).
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          <TextInput label="TRR (IBAN)" placeholder="SI56 …" {...polje('trrIban')} />
          <TextInput label="Prejemnik" description="Na UPN največ 33 znakov" {...polje('prejemnik')} />
          <TextInput label="Ulica in hišna številka" {...polje('ulica')} />
          <TextInput label="Pošta in kraj" placeholder="1000 Ljubljana" {...polje('kraj')} />
          <TextInput
            label="E-pošta za potrdila o plačilu"
            description="Stranke nam lahko pošljejo potrdilo za hitrejši vnos"
            {...polje('email')}
          />
          <NumberInput
            size="md"
            label="Strošek obdelave nakazila prek Stripa (€)"
            description="Odšteje se od točk ob vsakem nakazilu prek Stripa; 0 = brez"
            value={urejeno.stripeStrosek}
            onChange={(v) => setUrejeno({ ...urejeno, stripeStrosek: v })}
            min={0}
            decimalScale={2}
            decimalSeparator=","
            hideControls
            error={napake.stripeStrosek}
          />
        </SimpleGrid>
        {napaka && (
          <Alert color="red" variant="light" role="alert">
            {napaka}
          </Alert>
        )}
        <Group justify="space-between">
          <Text size="xs" c="dimmed">
            {n.spremenil ? `Nazadnje spremenil ${n.spremenil}, ${cas(n.spremenjeno)}` : ''}
          </Text>
          <Button onClick={() => void shrani()} loading={dela}>
            Shrani
          </Button>
        </Group>
      </Stack>
    </Kartica>
  );
}
